let canvasInstance = null;
let ctx = null;
let animationId = null;
let videos = new Map();
let gsapTickerId = null;
let forceRenderAll = ref(false);

export const useVideoCanvas = () => {
  const initCanvas = (canvasEl, gsap) => {
    if (canvasInstance) return;

    canvasInstance = canvasEl;
    ctx = canvasEl.getContext('2d', {
      alpha: true,
      desynchronized: true,
    });

    // Sync with GSAP ticker for smooth animations
    if (gsap) {
      gsapTickerId = gsap.ticker.add(render);
    } else {
      render();
    }
  };

  const registerVideo = (id, videoEl, bounds, containerEl) => {
    videos.set(id, {
      videoEl,
      bounds,
      isVisible: false,
      containerEl, // Store ref to container for bounds updates
    });
  };

  const unregisterVideo = id => {
    videos.delete(id);
  };

  const updateVideoBounds = (id, bounds, isVisible) => {
    const video = videos.get(id);
    if (video) {
      video.bounds = bounds;
      video.isVisible = isVisible;
    }
  };

  const setForceRender = force => {
    forceRenderAll.value = force;
  };

  const getForceRenderState = () => {
    return forceRenderAll.value;
  };

  const render = () => {
    if (!ctx || !canvasInstance) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvasInstance.width, canvasInstance.height);

    let renderedCount = 0;

    // Update bounds and draw all visible videos
    videos.forEach(video => {
      // Force render during transitions OR normal visibility check
      let shouldRender;

      if (forceRenderAll.value) {
        // During transitions: render ALL videos that have readyState > 0 (regardless of paused state)
        shouldRender = video.videoEl && video.videoEl.readyState >= 2;
      } else {
        // Normal mode: only visible and playing videos
        shouldRender =
          video.isVisible && video.videoEl && !video.videoEl.paused;
      }

      if (shouldRender) {
        // Update bounds every frame for smooth animations
        if (video.containerEl) {
          const rect = video.containerEl.getBoundingClientRect();
          video.bounds = {
            x: rect.left * window.devicePixelRatio,
            y: rect.top * window.devicePixelRatio,
            width: rect.width * window.devicePixelRatio,
            height: rect.height * window.devicePixelRatio,
          };
        }

        const { x, y, width, height } = video.bounds;

        try {
          // Calculate cumulative opacity from all parent elements
          let opacity = 1;
          if (video.containerEl) {
            let el = video.containerEl;
            while (el && el !== document.body) {
              const style = window.getComputedStyle(el);
              const elOpacity = parseFloat(style.opacity);
              if (!isNaN(elOpacity)) {
                opacity *= elOpacity;
              }
              el = el.parentElement;
            }
          }

          ctx.globalAlpha = opacity;
          ctx.drawImage(video.videoEl, x, y, width, height);
          ctx.globalAlpha = 1; // Reset
          renderedCount++;
        } catch (e) {
          console.warn('Failed to draw video:', e);
        }
      }
    });

    // Continue RAF if not using GSAP ticker
    if (!gsapTickerId) {
      animationId = requestAnimationFrame(render);
    }
  };

  const destroy = () => {
    if (animationId) {
      cancelAnimationFrame(animationId);
      animationId = null;
    }

    // Remove GSAP ticker
    if (gsapTickerId && typeof gsapTickerId === 'function') {
      gsapTickerId();
      gsapTickerId = null;
    }

    videos.clear();
    ctx = null;
    canvasInstance = null;
  };

  return {
    initCanvas,
    registerVideo,
    unregisterVideo,
    updateVideoBounds,
    setForceRender,
    getForceRenderState,
    destroy,
  };
};
